<script setup lang="ts">
  /**
   * PDFViewer Component with Batch Loading and Proper Zoom Calculation
   * A reusable component for displaying PDF documents in a modal dialog with optimized memory usage.
   *
   * Features:
   * - Modal PDF viewer with backdrop click to close
   * - Batch loading of pages to prevent memory issues
   * - Virtual scrolling for large documents
   * - Page navigation with input field and arrow buttons
   * - Page navigation with scroll position tracking
   * - Keyboard navigation (arrow keys for pages, Escape to close)
   * - Text highlighting
   * - Per-page rotation (90° increments)
   * - Loading state with progress
   * - Responsive design
   * - Fit page enabled by default for optimal viewing
   * - Automatic scanned PDF detection with fit page
   * - Accurate zoom level calculation and display
   * - Proper zoom transitions between fit page and manual zoom
   *
   * @example
   * ```vue
   *   <PDFViewer
   *     v-model:isOpen="isOpen"
   *     v-model:url="pdfUrl"
   *     :filename="filename"
   *     :highlight-text="'text to highlight'"
   *     :initial-page="1"
   *     :batch-size="5"
   *     @loaded="(pdfInstance) => console.log('PDF loaded', pdfInstance)"
   *     @page-change="(page) => console.log('Page changed', page)"
   *   />
   * ```
   *
   * @typedef {Object} Props
   * @property {string} url - URL of the PDF file to display
   * @property {string} filename - Name of the file to display in the header
   * @property {boolean} isOpen - Controls visibility of the modal
   * @property {number} [initialPage=1] - Initial page number to display
   * @property {string} [highlightText] - Text to highlight in the PDF
   * @property {number} [scale=1.1] - Scale factor for the PDF display
   * @property {boolean} [fitToWidth=true] - Whether to fit the PDF to the width of the container
   * @property {Object} [document] - Document metadata for scanned PDF detection
   * @property {number} [document.filesize] - File size in bytes (required for detection)
   * @property {number} [document.page_count] - Number of pages in the document (optional, improves detection accuracy)
   * @property {boolean} [annotation=false] - Whether to enable annotations
   * @property {string} [formId] - ID of the form to load annotations for
   * @property {boolean} [hideNavigation=false] - Whether to hide the navigation controls
   *
   * @typedef {Object} Emits
   * @property {(event: 'update:isOpen', value: boolean) => void} update:isOpen - Emitted when modal visibility changes
   * @property {(event: 'update:url', value: string) => void} update:url - Emitted when URL should be updated
   * @property {(event: 'loaded', pdfInstance: any) => void} loaded - Emitted when PDF is loaded
   * @property {(event: 'pageChange', page: number) => void} pageChange - Emitted when page changes
   * @property {(event: 'highlight', event: any) => void} highlight - Emitted on text highlight
   */

  import PDFNavigation from '@/components/pdf/genericPdfNav.vue'
  import { VuePDF, type AnnotationEventPayload } from '@tato30/vue-pdf'
  import { usePDFWithCmaps as usePDF } from '@/composables/usePDFWithCmaps'
  import { Maximize, RotateCcw, RotateCw, X, ZoomIn, ZoomOut } from 'lucide-vue-next'
  import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
  import { useNewPdfStore } from '@/store/newPdfStore'
  import { useSignatures } from '@/store/signatureStore'

  interface AnnotationData {
    id: string
    annotations: Record<string, { value: string }>
  }

  interface Props {
    url: string
    filename: string
    isOpen: boolean
    initialPage?: number
    highlightText?: string
    scale?: number
    fitPage?: boolean
    batchSize?: number // Number of pages to load in each batch
    preloadBuffer?: number // Number of pages to preload around current page
    document?: {
      filesize?: number
      page_count?: number
    }
    annotation?: boolean
    formId?: string
    hideNavigation?: boolean
    pdfData?: Uint8Array
  }

  const props = withDefaults(defineProps<Props>(), {
    batchSize: 5,
    preloadBuffer: 2,
    fitPage: true
  })

  const emit = defineEmits<{
    (e: 'update:isOpen', value: boolean): void
    (e: 'update:url', value: string): void
    (e: 'loaded', pdfInstance: any): void
    (e: 'pageChange', page: number): void
    (e: 'highlight', event: any): void
  }>()

  const signatureStore = useSignatures()
  const currentPage = ref(props.initialPage || 1)
  const isLoading = ref(true)
  const loadingProgress = ref(0)
  const internalUrl = ref(props.url)
  const currentScale = ref(props.scale ?? 1.0)
  const fitPage = ref(props.fitPage ?? true)
  const containerRef = ref<HTMLElement | null>(null)
  const scrollContainerRef = ref<HTMLElement | null>(null)
  const pageRefs = ref<Map<number, HTMLElement>>(new Map())
  const isScrolling = ref(false)
  const loadedPages = ref<Set<number>>(new Set())
  const pdfKey = ref(0)
  const hasInitialScrolled = ref(false)
  const pageRotations = ref<Map<number, number>>(new Map())

  // Track the actual rendered scale for each page (from viewport)
  const pageScales = ref<Map<number, number>>(new Map())
  const containerWidth = ref(0)

  // Batch loading state
  const visiblePages = ref<Set<number>>(new Set())
  const loadingQueue = ref<number[]>([])
  const isLoadingBatch = ref(false)
  const totalPages = ref(0)
  const pageHeights = ref<Map<number, string>>(new Map()) // Store estimated heights
  const loadingTimeout = ref<NodeJS.Timeout | null>(null)

  // Use internal URL for PDF loading with reactive key for forcing reloads
  const pdfSource = computed(() => {
    if (props.pdfData) {
      // Return the blob URL if it exists, otherwise return empty string
      return internalUrl.value.startsWith('blob:') ? internalUrl.value : ''
    }

    if (!internalUrl.value) return ''

    // Check if URL already has query parameters
    const separator = internalUrl.value.includes('?') ? '&' : '?'
    return `${internalUrl.value}${separator}key=${pdfKey.value}`
  })
  const { pdf, pages } = usePDF(pdfSource)

  // Calculate the effective zoom level for display
  const effectiveZoom = computed(() => {
    if (fitPage.value) {
      // Use the actual scale from the most recently loaded page
      const recentPageScale = pageScales.value.get(currentPage.value)
      if (recentPageScale) {
        return recentPageScale
      }
      // Fallback to first page scale if current page scale not available
      const firstPageScale = pageScales.value.get(1)
      return firstPageScale || currentScale.value
    }
    return currentScale.value
  })

  // Display zoom percentage
  const displayZoom = computed(() => {
    return Math.round(effectiveZoom.value * 100)
  })

  // Detect if PDF is scanned (image-based) based on filesize per page
  const isScannedPDF = computed(() => {
    const doc = props.document
    if (!doc || !doc.filesize) return false

    if (doc.page_count && doc.page_count > 0) {
      const fileSizePerPage = doc.filesize / doc.page_count
      return fileSizePerPage > 1000000
    }

    return doc.filesize > 5000000
  })

  const highlightOptions = computed(() => ({
    ignoreCase: true,
    completeWords: true
  }))

  // Calculate which pages should be visible based on current page and buffer
  const getVisiblePageRange = (centerPage: number) => {
    const start = Math.max(1, centerPage - props.preloadBuffer)
    const end = Math.min(totalPages.value, centerPage + props.preloadBuffer)
    return Array.from({ length: end - start + 1 }, (_, i) => start + i)
  }

  // Function to calculate container width
  function updateContainerWidth() {
    if (containerRef.value) {
      // Account for padding and margins
      const rect = containerRef.value.getBoundingClientRect()
      containerWidth.value = rect.width - 48 // Subtract padding (24px * 2)
      console.log('Container width updated:', containerWidth.value)
    }
  }

  // Watch for URL changes from parent
  watch(
    () => props.url,
    (newUrl, oldUrl) => {
      if (newUrl !== oldUrl) {
        if (internalUrl.value.startsWith('blob:')) {
          URL.revokeObjectURL(internalUrl.value)
        }

        internalUrl.value = newUrl
        if (newUrl) {
          fitPage.value = props.fitPage ?? true
          currentScale.value = props.scale ?? 1.0
          pageRotations.value.clear()
          pageScales.value.clear()
          hasInitialScrolled.value = false
          resetBatchLoading()
          // Force re-render when URL changes
          pdfKey.value++
        }
      }
    },
    { immediate: true }
  )

  watch(
    () => props.pdfData,
    (newPdfData, oldPdfData) => {
      if (newPdfData !== oldPdfData) {
        if (internalUrl.value.startsWith('blob:')) {
          URL.revokeObjectURL(internalUrl.value)
        }

        if (newPdfData) {
          const blob = new Blob([newPdfData], { type: 'application/pdf' })
          internalUrl.value = URL.createObjectURL(blob)

          fitPage.value = props.fitPage ?? true
          currentScale.value = props.scale ?? 1.0
          pageRotations.value.clear()
          pageScales.value.clear()
          hasInitialScrolled.value = false
          resetBatchLoading()

          pdfKey.value++
        } else {
          internalUrl.value = ''
        }
      }
    }
  )

  // Watch for modal open/close state
  watch(
    () => props.isOpen,
    (isOpen, wasOpen) => {
      if (isOpen && !wasOpen && (props.url || props.pdfData)) {
        // Modal is opening - force complete reset
        console.log('Modal opening, resetting PDF viewer')
        isLoading.value = true
        totalPages.value = 0
        resetBatchLoading()

        // Prevent background scrolling
        document.body.style.overflow = 'hidden'

        // Reset zoom calculation values
        pageScales.value.clear()

        // Force PDF reload by changing the key
        pdfKey.value++

        // Set URL after key change to trigger usePDF reload
        nextTick(() => {
          if (props.pdfData) {
            // If we have PDF data, create a blob URL
            const blob = new Blob([props.pdfData], { type: 'application/pdf' })
            internalUrl.value = URL.createObjectURL(blob)
          } else {
            internalUrl.value = props.url
          }
          updateContainerWidth()
        })
      } else if (!isOpen && wasOpen) {
        // Modal is closing - restore background scrolling
        document.body.style.overflow = ''
        signatureStore.cleanup()
      }
    }
  )

  watch(
    () => props.fitPage,
    (newValue, oldValue) => {
      if (newValue !== oldValue) {
        fitPage.value = newValue
        // Clear stored page heights when fit page changes to prevent spacing issues
        pageHeights.value.clear()
      }
    }
  )

  // Watch for scale prop changes from parent
  watch(
    () => props.scale,
    (newScale, oldScale) => {
      if (newScale !== oldScale && newScale !== undefined) {
        currentScale.value = newScale
        // Clear stored page heights when scale changes to prevent spacing issues
        pageHeights.value.clear()
      }
    }
  )

  // Watch for initialPage changes from parent
  watch(
    () => props.initialPage,
    (newPage) => {
      if (newPage && newPage !== currentPage.value) {
        currentPage.value = newPage
        hasInitialScrolled.value = false
        updateVisiblePages(newPage)
        if (!isLoading.value && pageRefs.value.size > 0) {
          nextTick(() => {
            setTimeout(() => {
              scrollToPage(newPage)
            }, 100)
          })
        }
      }
    }
  )

  // Auto-enable fit page for scanned PDFs (now enabled by default)
  watch(
    [isScannedPDF, () => props.url],
    ([isScanned, url]) => {
      // Since fit page is now enabled by default, we only need to ensure it stays enabled for scanned PDFs
      if (url && isScanned && !fitPage.value) {
        fitPage.value = true
        // Don't increment pdfKey - just let the component re-render
      }
    },
    { immediate: true }
  )

  // Watch current page changes to update visible pages
  watch(currentPage, (newPage) => {
    updateVisiblePages(newPage)
  })

  // Watch for PDF changes to reset loading state
  watch(pdf, (newPdf, oldPdf) => {
    console.log('PDF changed:', { newPdf: !!newPdf, oldPdf: !!oldPdf, pages: pages.value })
    if (newPdf && newPdf !== oldPdf) {
      console.log('New PDF loaded, pages count:', pages.value)
      isLoading.value = true
      totalPages.value = 0
      resetBatchLoading()

      // Check if pages are already available
      if (pages.value && pages.value > 0) {
        console.log('Pages immediately available:', pages.value)
        totalPages.value = pages.value
        nextTick(() => {
          initializeBatchLoading()
        })
      }
    } else if (!newPdf && oldPdf) {
      // PDF was cleared
      console.log('PDF cleared')
      isLoading.value = true
      totalPages.value = 0
      resetBatchLoading()
    }
  })

  // Watch for pages count to initialize batch loading
  watch(
    pages,
    (newPages, oldPages) => {
      if (newPages && newPages > 0 && newPages !== oldPages) {
        totalPages.value = newPages
        nextTick(() => {
          initializeBatchLoading()
        })
      }
    },
    { immediate: true }
  )

  function resetBatchLoading() {
    loadedPages.value.clear()
    visiblePages.value.clear()
    loadingQueue.value = []
    isLoadingBatch.value = false
    loadingProgress.value = 0
    pageHeights.value.clear()

    // Clear any existing loading timeout
    if (loadingTimeout.value) {
      clearTimeout(loadingTimeout.value)
      loadingTimeout.value = null
    }

    console.log('Reset batch loading and cleared page heights')
  }

  function initializeBatchLoading() {
    console.log('Initializing batch loading, totalPages:', totalPages.value)
    // Start with the initial page and its buffer
    const initialPage = props.initialPage || currentPage.value || 1
    console.log('Initial page:', initialPage)

    // Ensure the current page is set to the initial page
    currentPage.value = initialPage

    updateVisiblePages(initialPage)

    // Set a fallback timeout to hide loading state if it gets stuck
    if (loadingTimeout.value) {
      clearTimeout(loadingTimeout.value)
    }
    loadingTimeout.value = setTimeout(() => {
      if (isLoading.value) {
        console.log('Loading timeout reached, forcing loading state to false')
        isLoading.value = false
      }
    }, 10000) // 10 second timeout
  }

  function updateVisiblePages(centerPage: number) {
    console.log('Updating visible pages for center page:', centerPage)
    const newVisiblePages = new Set(getVisiblePageRange(centerPage))
    console.log('New visible pages:', Array.from(newVisiblePages))

    // Add pages that should be visible but aren't loaded yet to the queue
    const pagesToLoad = Array.from(newVisiblePages).filter((page) => !loadedPages.value.has(page))
    console.log('Pages to load:', pagesToLoad)

    if (pagesToLoad.length > 0) {
      // Add to queue if not already there
      pagesToLoad.forEach((page) => {
        if (!loadingQueue.value.includes(page)) {
          loadingQueue.value.push(page)
        }
      })

      // Process the queue
      processLoadingQueue()
    }

    visiblePages.value = newVisiblePages
    console.log('Visible pages set to:', Array.from(visiblePages.value))
  }

  async function processLoadingQueue() {
    if (isLoadingBatch.value || loadingQueue.value.length === 0) {
      console.log('Skipping queue processing:', {
        isLoadingBatch: isLoadingBatch.value,
        queueLength: loadingQueue.value.length
      })
      return
    }

    console.log('Processing loading queue:', loadingQueue.value)
    isLoadingBatch.value = true

    // Process pages in batches
    while (loadingQueue.value.length > 0) {
      const batch = loadingQueue.value.splice(0, props.batchSize)
      console.log('Loading batch:', batch)

      // Load this batch
      await loadPageBatch(batch)

      // Update progress
      loadingProgress.value = Math.round((loadedPages.value.size / totalPages.value) * 100)
      console.log('Loading progress:', loadingProgress.value + '%')

      // Small delay to prevent blocking the UI
      await new Promise((resolve) => setTimeout(resolve, 50))
    }

    isLoadingBatch.value = false
    console.log('Queue processing complete')
  }

  async function loadPageBatch(pageNumbers: number[]) {
    console.log('Loading page batch:', pageNumbers)
    // For this implementation, we're marking pages as "loaded" when they're in the visible set
    // The actual VuePDF component will handle the loading when it's rendered
    pageNumbers.forEach((page) => {
      if (page >= 1 && page <= totalPages.value) {
        loadedPages.value.add(page)
        console.log('Marked page as loaded:', page)
      }
    })

    console.log('All loaded pages:', Array.from(loadedPages.value))

    // Small delay to allow DOM updates
    await new Promise((resolve) => setTimeout(resolve, 10))
  }
  const pdfStore = useNewPdfStore()
  const withAnnotations = props.annotation
  const pdfStateAnnotationMap = computed(() => pdfStore.getAnnotationsMap(props.formId || ''))

  const onAnnotation = (payload: AnnotationEventPayload) => {
    // Handle link annotations by opening in new tab
    if (payload.type === 'link') {
      if (payload.data.url) {
        window.open(payload.data.url, '_blank')
      }
      return
    }

    if (!withAnnotations || !props.formId) return
    const { fieldName, value } = payload.data
    pdfStore.updateAnnotation(props.formId, fieldName, value)
    pdfStore.setManuallyEdited(props.formId, true)
  }

  function closeViewer() {
    console.log('Closing PDF viewer')

    if (props.formId && props.annotation) {
      saveSignaturesToStore()
    }

    // Restore background scrolling
    document.body.style.overflow = ''

    emit('update:isOpen', false)

    if (internalUrl.value.startsWith('blob:')) {
      URL.revokeObjectURL(internalUrl.value)
    }

    // Clear URL immediately to force usePDF to reset
    internalUrl.value = ''

    if (props.url) {
      emit('update:url', '')
    }

    // Reset all state
    currentPage.value = props.initialPage || 1
    isLoading.value = true
    totalPages.value = 0
    resetBatchLoading()
    pageRotations.value.clear()
    pageScales.value.clear()
    hasInitialScrolled.value = false
    pageRefs.value.clear()
    containerWidth.value = 0
  }

  function saveSignaturesToStore() {
    if (!props.formId) return

    const formSignatures = signatureStore.getFormSignatures(props.formId)

    if (Object.keys(formSignatures).length > 0) {
      pdfStore.updateSignatures(props.formId, formSignatures)
    }
  }

  function onPdfLoaded(viewport: any, pageNumber: number) {
    console.log('PDF page loaded:', pageNumber, 'viewport:', viewport)

    // Store the actual scale from the viewport
    if (viewport && viewport.scale) {
      pageScales.value.set(pageNumber, viewport.scale)
      console.log(`Stored scale for page ${pageNumber}: ${viewport.scale}`)
    }

    // Store the actual height of loaded pages for better placeholders
    nextTick(() => {
      setTimeout(() => {
        const pageElement = pageRefs.value.get(pageNumber)
        if (pageElement) {
          const height = pageElement.offsetHeight
          if (height > 0) {
            pageHeights.value.set(pageNumber, `${height}px`)
            console.log(`Stored height for page ${pageNumber}: ${height}px`)
          }
        }
      }, 50)
    })

    // Handle signature fields when the first page loads
    if (pageNumber === 1 && props.annotation && props.formId) {
      try {
        if (pdf.value) {
          pdf.value.promise
            .then(async (pdfDoc: any) => {
              if (pdfDoc && typeof pdfDoc.getFieldObjects === 'function') {
                const fieldObjects = await pdfDoc.getFieldObjects()
                if (fieldObjects) {
                  await signatureStore.updateAll(fieldObjects as any)
                  await signatureStore.findAllSignatureFields(fieldObjects as any)
                }
              }
            })
            .catch((error: any) => {
              console.warn('Error handling signature fields:', error)
            })
        }
      } catch (error) {
        console.warn('Error handling signature fields:', error)
      }
    }

    // Mark this specific page as loaded
    const targetPage = props.initialPage || currentPage.value || 1

    // Hide loading state when the target page loads OR when the first page loads (whichever comes first)
    if (pageNumber === targetPage || loadedPages.value.size === 1) {
      console.log(
        `Target page ${targetPage} or first page loaded (loaded page: ${pageNumber}), hiding loading state`
      )
      isLoading.value = false

      // Clear the loading timeout since we successfully loaded
      if (loadingTimeout.value) {
        clearTimeout(loadingTimeout.value)
        loadingTimeout.value = null
      }

      emit('loaded', viewport)
    }

    // Handle scrolling to initial page when it loads
    if (props.initialPage && pageNumber === props.initialPage && !hasInitialScrolled.value) {
      hasInitialScrolled.value = true
      console.log(`Initial page ${pageNumber} loaded, scrolling to it`)
      nextTick(() => {
        setTimeout(() => {
          scrollToPage(pageNumber)
        }, 100) // Reduced delay since the page is definitely loaded now
      })
    }
  }

  function toggleFitPage() {
    if (fitPage.value) {
      // Switching from fit page to manual zoom
      // Set the current scale to the effective zoom level
      currentScale.value = effectiveZoom.value
      fitPage.value = false
      console.log('Switched to manual zoom at:', currentScale.value)
    } else {
      // Switching to fit page
      fitPage.value = true
      console.log('Switched to fit page')
    }

    // Clear stored page heights when scale changes to prevent spacing issues
    pageHeights.value.clear()
  }

  function zoomIn() {
    if (fitPage.value) {
      // If currently in fit page mode, switch to manual and start from effective zoom
      currentScale.value = Math.min(5, effectiveZoom.value + 0.1)
      fitPage.value = false
      console.log('Zoomed in from fit page to:', currentScale.value)
    } else {
      currentScale.value = Math.min(5, currentScale.value + 0.1)
      console.log('Zoomed in to:', currentScale.value)
    }

    // Clear stored page heights when scale changes to prevent spacing issues
    pageHeights.value.clear()
  }

  function zoomOut() {
    if (fitPage.value) {
      // If currently in fit page mode, switch to manual and start from effective zoom
      currentScale.value = Math.max(0.1, effectiveZoom.value - 0.1)
      fitPage.value = false
      console.log('Zoomed out from fit page to:', currentScale.value)
    } else {
      currentScale.value = Math.max(0.1, currentScale.value - 0.1)
      console.log('Zoomed out to:', currentScale.value)
    }

    // Clear stored page heights when scale changes to prevent spacing issues
    pageHeights.value.clear()
  }

  function resetZoom() {
    fitPage.value = false
    currentScale.value = props.scale ?? 1.0

    // Clear stored page heights when scale changes to prevent spacing issues
    pageHeights.value.clear()
  }

  function rotateCurrentPage() {
    const currentRotation = pageRotations.value.get(currentPage.value) || 0
    const newRotation = (currentRotation + 90) % 360
    pageRotations.value.set(currentPage.value, newRotation)

    // Clear the stored height for this page so it gets recalculated with new rotation
    pageHeights.value.delete(currentPage.value)
    console.log(
      `Page ${currentPage.value} rotated to ${newRotation}°, cleared stored height for recalculation`
    )

    // Force height recalculation after a short delay to allow the rotation to render
    nextTick(() => {
      setTimeout(() => {
        const pageElement = pageRefs.value.get(currentPage.value)
        if (pageElement) {
          const height = pageElement.offsetHeight
          if (height > 0) {
            pageHeights.value.set(currentPage.value, `${height}px`)
            console.log(`Recalculated height for rotated page ${currentPage.value}: ${height}px`)
          }
        }
      }, 100) // Small delay to ensure rotation has been applied
    })
  }

  function getPageRotation(pageNumber: number): number {
    return pageRotations.value.get(pageNumber) || 0
  }

  // Calculate container width on resize and setup scroll tracking
  let resizeObserver: ResizeObserver | null = null

  onMounted(() => {
    if (containerRef.value) {
      resizeObserver = new ResizeObserver(() => {
        updateContainerWidth()
      })
      resizeObserver.observe(containerRef.value)
    }

    document.addEventListener('keydown', handleKeydown)

    signatureStore.setPdfStore('new')

    // Initial container width calculation
    updateContainerWidth()
  })

  onUnmounted(() => {
    if (resizeObserver && containerRef.value) {
      resizeObserver.unobserve(containerRef.value)
      resizeObserver.disconnect()
    }

    // Clear loading timeout
    if (loadingTimeout.value) {
      clearTimeout(loadingTimeout.value)
      loadingTimeout.value = null
    }

    // Restore background scrolling in case component is unmounted while modal is open
    document.body.style.overflow = ''

    if (internalUrl.value.startsWith('blob:')) {
      URL.revokeObjectURL(internalUrl.value)
    }

    document.removeEventListener('keydown', handleKeydown)
    pageRefs.value.clear()
  })

  function updateCurrentPage(newPage: number) {
    currentPage.value = newPage
    emit('pageChange', newPage)

    // If user navigates while loading and the target page is already loaded, hide loading
    if (isLoading.value && loadedPages.value.has(newPage)) {
      console.log('Navigation to loaded page, hiding loading state')
      isLoading.value = false

      // Clear the loading timeout since we successfully loaded
      if (loadingTimeout.value) {
        clearTimeout(loadingTimeout.value)
        loadingTimeout.value = null
      }
    }

    scrollToPage(newPage)
  }

  function scrollToPage(pageNumber: number) {
    console.log(`Attempting to scroll to page ${pageNumber}`)
    if (!scrollContainerRef.value) {
      console.log('No scroll container ref available')
      return
    }

    const pageElement = pageRefs.value.get(pageNumber)
    if (pageElement) {
      console.log(`Found page element for page ${pageNumber}, scrolling`)
      isScrolling.value = true
      pageElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      })
      setTimeout(() => {
        isScrolling.value = false
      }, 1000)
    } else {
      console.log(
        `Page element for page ${pageNumber} not found. Available pages:`,
        Array.from(pageRefs.value.keys())
      )
      // Retry after a short delay if the page element isn't ready yet
      setTimeout(() => {
        const retryPageElement = pageRefs.value.get(pageNumber)
        if (retryPageElement) {
          console.log(`Retry successful: scrolling to page ${pageNumber}`)
          isScrolling.value = true
          retryPageElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          })
          setTimeout(() => {
            isScrolling.value = false
          }, 1000)
        } else {
          console.log(`Retry failed: page ${pageNumber} element still not found`)
        }
      }, 500)
    }
  }

  function setPageRef(pageNumber: number, el: HTMLElement | null) {
    if (el) {
      pageRefs.value.set(pageNumber, el)
    } else {
      pageRefs.value.delete(pageNumber)
    }
  }

  function handleScroll() {
    if (isScrolling.value || !scrollContainerRef.value) return

    const container = scrollContainerRef.value
    const containerTop = container.scrollTop
    const containerHeight = container.clientHeight
    const centerY = containerTop + containerHeight / 2

    let closestPage = 1
    let closestDistance = Infinity

    pageRefs.value.forEach((pageElement, pageNumber) => {
      const rect = pageElement.getBoundingClientRect()
      const containerRect = container.getBoundingClientRect()
      const pageTop = rect.top - containerRect.top + containerTop
      const pageCenter = pageTop + rect.height / 2
      const distance = Math.abs(pageCenter - centerY)

      if (distance < closestDistance) {
        closestDistance = distance
        closestPage = pageNumber
      }
    })

    if (closestPage !== currentPage.value) {
      currentPage.value = closestPage
      emit('pageChange', closestPage)
    }
  }

  function onHighlight(event: any) {
    emit('highlight', event)
  }

  function handleBackdropClick(event: MouseEvent) {
    if (event.target === event.currentTarget) {
      closeViewer()
    }
  }

  function handleKeydown(event: KeyboardEvent) {
    if (!props.isOpen || (event.target as HTMLElement)?.tagName === 'INPUT') {
      return
    }

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault()
        if (currentPage.value > 1) {
          updateCurrentPage(currentPage.value - 1)
        }
        break
      case 'ArrowRight':
        event.preventDefault()
        if (currentPage.value < pages.value) {
          updateCurrentPage(currentPage.value + 1)
        }
        break
      case 'Escape':
        event.preventDefault()
        closeViewer()
        break
    }
  }

  // Check if a page should be rendered (is in visible range)
  function shouldRenderPage(pageNumber: number): boolean {
    return visiblePages.value.has(pageNumber)
  }

  // Get estimated height for a page (for placeholders)
  function getPageHeight(pageNumber: number): string {
    // Use stored height if available, otherwise estimate based on scale and fit page
    if (pageHeights.value.has(pageNumber)) {
      return pageHeights.value.get(pageNumber)!
    }

    // Default height estimation based on common PDF page sizes
    // Use a more conservative estimate to prevent large gaps
    let baseHeight = fitPage.value ? 600 : 500 * currentScale.value

    // Adjust height estimation for rotated pages (90° and 270° rotations swap width/height)
    const rotation = getPageRotation(pageNumber)
    if (rotation === 90 || rotation === 270) {
      // For rotated pages, the height might be different (landscape vs portrait)
      // Adjust the estimation accordingly
      baseHeight = fitPage.value ? 450 : 400 * currentScale.value
    }

    return `${baseHeight}px`
  }

  // Load a specific page (when user clicks placeholder)
  function loadSpecificPage(pageNumber: number) {
    if (!visiblePages.value.has(pageNumber)) {
      visiblePages.value.add(pageNumber)
      if (!loadingQueue.value.includes(pageNumber)) {
        loadingQueue.value.unshift(pageNumber) // Add to front of queue for immediate loading
        processLoadingQueue()
      }
    }
  }
</script>

<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 z-[60] flex items-center justify-center bg-gray-900/50 backdrop-blur-sm"
    @click="handleBackdropClick"
  >
    <div
      ref="containerRef"
      class="flex h-[95vh] w-[45vw] flex-col overflow-hidden rounded-lg bg-white shadow-lg"
    >
      <!-- Header -->
      <div
        class="flex h-14 shrink-0 items-center justify-between border-b border-gray-200 bg-gray-50 px-6"
      >
        <h2 class="truncate text-base font-medium text-gray-900">{{ filename }}</h2>
        <button
          type="button"
          class="inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-400 transition
            hover:bg-gray-100 hover:text-gray-500"
          @click="closeViewer"
        >
          <span class="sr-only">Close</span>
          <X class="h-5 w-5" />
        </button>
      </div>

      <!-- Content -->
      <div class="relative flex-1 overflow-hidden bg-white">
        <div
          v-if="isLoading"
          class="absolute inset-0 z-10 flex items-center justify-center bg-white/90"
        >
          <div class="flex flex-col items-center gap-3">
            <div
              class="h-4 w-4 animate-spin rounded-full border-2 border-blue-600 border-t-transparent"
            ></div>
            <div class="text-center">
              <div class="text-sm font-medium text-gray-600">Loading document...</div>
              <div
                v-if="loadingProgress > 0"
                class="mt-1 text-xs text-gray-500"
              >
                {{ loadingProgress }}% loaded
              </div>
            </div>
          </div>
        </div>
        <div
          ref="scrollContainerRef"
          class="h-full w-full overflow-auto"
          @scroll="handleScroll"
        >
          <div class="flex min-h-full w-full flex-col items-center justify-start py-6">
            <div class="w-full max-w-3xl space-y-4 px-6">
              <div
                v-for="page in pages"
                :key="`page-${page}`"
                :ref="(el) => setPageRef(page, el as HTMLElement)"
                class="pdf-page-container"
                :style="{ minHeight: getPageHeight(page) }"
              >
                <VuePDF
                  v-if="shouldRenderPage(page)"
                  :key="`pdf-${pdfKey}-${page}-${fitPage ? 'fit' : currentScale}-${getPageRotation(page)}`"
                  :pdf="pdf"
                  :page="page"
                  :scale="fitPage ? undefined : currentScale"
                  :rotation="getPageRotation(page)"
                  :highlight-text="highlightText"
                  :highlight-options="highlightOptions"
                  :fit-parent="fitPage"
                  text-layer
                  :annotation-layer="withAnnotations"
                  :annotations-map="withAnnotations ? pdfStateAnnotationMap : {}"
                  class="mx-auto w-full shadow-lg"
                  :style="{
                    maxWidth: fitPage ? '100%' : 'none',
                    height: 'auto'
                  }"
                  @loaded="(viewport) => onPdfLoaded(viewport, page)"
                  @highlight="onHighlight"
                  @annotation="onAnnotation"
                />
                <div
                  v-else
                  class="flex cursor-pointer items-center justify-center rounded-lg border-2 border-dashed border-gray-200
                    bg-gray-50 transition-colors hover:bg-gray-100"
                  :style="{ minHeight: getPageHeight(page) }"
                  @click="loadSpecificPage(page)"
                >
                  <div class="text-center">
                    <div class="mb-1 text-sm text-gray-500">Page {{ page }}</div>
                    <div class="text-xs text-gray-400">Click to load</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div
        class="flex h-14 shrink-0 items-center justify-between border-t border-gray-200 bg-white px-6"
      >
        <!-- Zoom controls -->
        <div class="flex items-center space-x-2">
          <button
            class="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 bg-white
              text-gray-700 hover:bg-gray-50"
            title="Zoom out"
            @click="zoomOut"
          >
            <span class="sr-only">Zoom out</span>
            <ZoomOut class="h-4 w-4" />
          </button>

          <span class="text-sm text-gray-600">{{ displayZoom }}%</span>

          <button
            class="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 bg-white
              text-gray-700 hover:bg-gray-50"
            title="Zoom in"
            @click="zoomIn"
          >
            <span class="sr-only">Zoom in</span>
            <ZoomIn class="h-4 w-4" />
          </button>

          <button
            class="ml-1 inline-flex h-8 items-center rounded-md border border-gray-300 bg-white px-3 text-xs
              text-gray-700 hover:bg-gray-50"
            title="Reset zoom"
            @click="resetZoom"
          >
            <RotateCcw class="mr-1 inline h-3 w-3" />
            Reset
          </button>

          <button
            class="ml-1 inline-flex h-8 items-center rounded-md border border-gray-300 px-3 text-xs hover:bg-gray-50"
            :class="
              fitPage ? 'border-purple-300 bg-purple-100 text-purple-700' : 'bg-white text-gray-700'
            "
            title="Fit page"
            @click="toggleFitPage"
          >
            <Maximize class="mr-1 inline h-3 w-3" />
            Fit Page
          </button>

          <button
            class="ml-1 inline-flex h-8 items-center rounded-md border border-gray-300 bg-white px-3 text-xs
              text-gray-700 hover:bg-gray-50"
            title="Rotate current page"
            @click="rotateCurrentPage"
          >
            <RotateCw class="mr-1 inline h-3 w-3" />
            Rotate
          </button>
        </div>

        <!-- Page navigation -->
        <PDFNavigation
          v-if="!hideNavigation"
          :current-page="currentPage"
          :total-pages="pages"
          :show-prev-next-buttons="true"
          :show-page-buttons="true"
          @update-page="updateCurrentPage"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
  .overflow-auto {
    scroll-behavior: smooth;
  }

  .overflow-auto::-webkit-scrollbar {
    width: 8px;
  }

  .overflow-auto::-webkit-scrollbar-track {
    @apply rounded-lg bg-gray-100;
  }

  .overflow-auto::-webkit-scrollbar-thumb {
    @apply rounded-lg bg-gray-300;
  }

  .overflow-auto::-webkit-scrollbar-thumb:hover {
    @apply bg-gray-400;
  }

  .pdf-page-container :deep(.linkAnnotation) {
    cursor: pointer;
  }
</style>
